<template>
    <div class="p-4 text-sm">
        <h2 class="text-gray mb-4">Скопировать расписание на месяц</h2>
       <div class="flex items-center gap-1 mb-4 relative">
           <div class="w-6 h-6 flex justify-center items-center cursor-pointer" @click="prevMonth">
               <icon width="9" height="13"><IconChevronLeft /></icon>
           </div>
           <div
               class="rounded bg-grayxlight h-[26px] w-36 text-center pt-1 first-letter:uppercase cursor-pointer select-none"
               @click="isMonthPickerOpen = true"
           >
               {{ selectedMonthLabel }}
           </div>
           <div class="w-6 h-6 flex justify-center items-center cursor-pointer" @click="nextMonth">
               <icon width="9" height="13"><IconChevronRight /></icon>
           </div>
           <div v-if="isMonthPickerOpen" class="fixed inset-0 z-40" @click="isMonthPickerOpen = false" />
           <div v-if="isMonthPickerOpen" class="absolute top-full mt-1 left-1/2 -translate-x-1/2 z-50 bg-white rounded-xl shadow-lg p-4 w-48">
               <AppCalendar
                   :model-value="newDate"
                   precision="month"
                   @update:model-value="val => { newDate = val as Dayjs; isMonthPickerOpen = false }"
               />
           </div>
       </div>
       <ScheduleSelect :schedules @selected="onSelect" class="max-h-[50vh] overflow-scroll mb-8" />
       <div class="flex justify-end">
        <btn @click="copy" :disabled="isDisabled || isCopyLoading" :class="{'opacity-50': isDisabled || isCopyLoading}">Копировать</btn>
       </div>
    </div>

    <ShiftCopyModal
        v-if="copyProcess.isModalOpen.value"
        :mode="copyProcess.modalMode.value"
        :errors="copyProcess.modalErrors.value"
        :preview="copyProcess.preview.value"
        :is-loading="copyProcess.isLoading.value"
        @close="copyProcess.close"
        @confirm="copyProcess.confirm"
    />
</template>

<script setup lang="ts">
import dayjs, { type Dayjs } from 'dayjs'
import { ref, computed } from 'vue'
import type { Schedule } from '@/types'
import AppCalendar from '@/ui/AppCalendar.vue'
import IconChevronLeft from '@/ui/icons/IconChevronLeft.vue'
import IconChevronRight from '@/ui/icons/IconChevronRight.vue'
import ScheduleSelect from './ScheduleSelect.vue'
import { useToast } from 'vue-toast-notification'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useAuthStore } from '@/stores/authStore'
import ShiftCopyModal from './ShiftCopyModal.vue'
import { useShiftCopy } from './useShiftCopy'

const scheduleStore = useScheduleStore()
const authStore = useAuthStore()
const copyProcess = useShiftCopy()
const emit = defineEmits<{
    copied: []
}>()

const toast = useToast({position: 'top'})
const newDate = ref<Dayjs | undefined>()
const isMonthPickerOpen = ref(false)

const selectedMonthLabel = computed(() => newDate.value ? newDate.value.format('MMMM YYYY') : 'Выберите месяц')

function prevMonth() {
    newDate.value = (newDate.value ?? dayjs()).subtract(1, 'month').startOf('month')
}
function nextMonth() {
    newDate.value = (newDate.value ?? dayjs()).add(1, 'month').startOf('month')
}
const schedules = computed(() => {
    const departmentId = scheduleStore.currentDepartment?.id
    return scheduleStore.schedules.filter(item => (
        item.department.id === departmentId
        && !item.is_vacancy
        && !item.is_outsource
        && item.user.employments.some(employment => employment.id === item.id && employment.department === departmentId)
        && item.shifts.length
    ))
})
type CopySchedule = Pick<Schedule, 'id'>
const schedulesCopyList = ref<CopySchedule[]>(schedules.value)

const isDisabled = computed(() => (
    !newDate.value
    || !schedulesCopyList.value.length
    || !authStore.permissions?.can_create_schedule
    || dayjs(scheduleStore.currentScheduleDate).format('MM-YYYY') === dayjs(newDate.value).format('MM-YYYY')
))
const isCopyLoading = computed(() => copyProcess.isLoading.value)


const onSelect = (schedules: CopySchedule[]) => {
    schedulesCopyList.value = schedules
}

const copyDatePairs = (targetMonth: Dayjs) => {
    const sourceMonth = dayjs(scheduleStore.currentScheduleDate).startOf('month')
    const targetStart = targetMonth.startOf('month')
    const delta = sourceMonth.day() - targetStart.day()

    return Array.from({ length: sourceMonth.daysInMonth() }, (_, index) => sourceMonth.add(index, 'day'))
        .map(sourceDate => ({ sourceDate, targetDate: targetStart.date(sourceDate.date() + delta) }))
        .filter(({ targetDate }) => targetDate.isSame(targetStart, 'month'))
}

const copy = async () => {
    if (!newDate.value) return
    if (!authStore.permissions?.can_create_schedule) {
        toast.error('У вас нет прав на данное действие')
        return
    }

    const targetMonth = newDate.value.startOf('month')
    const pairs = copyDatePairs(targetMonth)
    const departmentId = scheduleStore.currentDepartment?.id
    if (!pairs.length || !departmentId) return

    const sourceSchedules = schedulesCopyList.value
        .map(({ id }) => scheduleStore.schedules.find(schedule => schedule.id === id))
        .filter((schedule): schedule is Schedule => Boolean(schedule))
    if (!sourceSchedules.length) {
        toast.error('Нет доступных сотрудников для копирования')
        return
    }

    await copyProcess.start({
        department_id: departmentId,
        employment_ids: sourceSchedules.map(schedule => schedule.id),
        source_dates: pairs.map(({ sourceDate }) => sourceDate.format('YYYY-MM-DD')),
        target_dates: pairs.map(({ targetDate }) => targetDate.format('YYYY-MM-DD')),
    }, () => {
        scheduleStore.currentScheduleDate = targetMonth
        emit('copied')
    }, { confirmOnPreview: true })
}
</script>
